import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ResponseCode } from '@lsq-frontend/remote-services/enterprise/common';
import { ProspectData } from '@lsq-frontend/remote-services/ar-app';
import { DrApplicationService } from '@lsqlabs/dr-application-shared';
import { ErrorToastBlacklistService } from '@lsq-frontend/angular-shared/features/error-toast-blacklist-service';
import { Modal, ModalService } from '@lsq-frontend/common-ui';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { OperationService } from '@lsq-frontend/remote-services/ar-app';
import { MigrateProspectModalComponent } from './migrate-prospect-modal/migrate-prospect-modal.component';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { ManageProspectComponent } from '../manage-prospect.component';

@Component({
    selector: 'prospect-migration',
    styleUrls: ['./prospect-migration.component.scss'],
    templateUrl: './prospect-migration.component.html'
})
export class ManageProspectMigrationComponent {
    public prospect: ProspectData = { ...this.arService.getProspect() };
    public tooltipMessage =
        'This will migrate the account contact to a FastTrack user. This action cannot be undone.';
    public userEmail: string;
    public migrationSuccessMessage = 'The prospect has been successfully migrated.';
    constructor(
        private _modalService: ModalService,
        private arService: DrApplicationService,
        private _operationService: OperationService,
        private _userService: UserService,
        private _alertService: AlertService,
        private _errorToastBlacklistService: ErrorToastBlacklistService,
        private _manageProspectComponent: ManageProspectComponent
    ) {
        this._userService.user$.pipe().subscribe((user) => {
            this.userEmail = user.emailAddress;
        });
        this._errorToastBlacklistService.registerToastBlacklistedErrorCode(
            '/v1/prospectData/migrate',
            ResponseCode.Error
        );
    }

    public openMigrationModal() {
        const formGroup: FormGroup = new FormGroup({
            clientNumber: new FormControl('', [Validators.required])
        });
        const isSubmittingSubject = new BehaviorSubject<boolean>(false);

        const okButtonIsDisabled$ = formGroup.valueChanges.pipe(
            map((_) => !formGroup.valid),
            startWith(!formGroup.valid)
        );

        this._modalService.showModal(
            new Modal({
                title: 'Migrate User to FastTrack',
                bodyComponent: MigrateProspectModalComponent,
                showCloseButton: true,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Migrate User',
                clickOutsideToClose: false,
                okButtonIsDisabled$,
                okButtonSize: 'block',
                showCancelButton: true,
                data: formGroup,
                width: '660px',
                isSubmitting$: isSubmittingSubject,
                onCancelClick: () => this._modalService.closeModal(),
                onOkClick: async () => {
                    isSubmittingSubject.next(true);
                    await this.migrateProspect(formGroup);
                    isSubmittingSubject.next(false);
                    this.prospect = await this.arService.getProspect();
                    if (this.prospect.id) {
                        this._manageProspectComponent.setTabsConfig(this.prospect.id);
                    }
                }
            })
        );
    }

    public async migrateProspect(formGroup: FormGroup): Promise<void> {
        const payload = {
            prospectId: this.prospect.id,
            clientNumber: formGroup.controls.clientNumber.value,
            migratedBy: this.userEmail
        };
        try {
            formGroup.setErrors({});
            const migrateResponse = await this._operationService
                .migrateProspectAndUser(
                    payload.prospectId,
                    payload.clientNumber,
                    payload.migratedBy
                )
                .toPromise();
            await this.arService.setProspect(migrateResponse);
            this._alertService.success(this.migrationSuccessMessage);
            this._modalService.closeModal();
        } catch (e) {
            formGroup.setErrors({ err: e.message });
        }
    }

    public isInvitedOrMigrated(): boolean {
        return (
            this.prospect.status == ProspectData.StatusEnum.Invited ||
            this.prospect.status == ProspectData.StatusEnum.Migrated
        );
    }

    public parseDate(date: Date): string {
        return date != null ? moment(date).format(DateFormat) : '--';
    }
}

export const DateFormat = 'MM/DD/YY [at] h:mm A [ET]';
